import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import { Button, Card, EmptyState, tokens } from './ui/controls';

type QueryField = { name: string; kind: string; examples: string[] };

type Suggestion = { label: string; insert: string; replaceFrom: number };

const FALLBACK_FIELDS: QueryField[] = [
  { name: 'id', kind: 'number', examples: [] },
  { name: 'type', kind: 'text', examples: [] },
  { name: 'file_location', kind: 'text', examples: [] },
  { name: 'robot_type', kind: 'text', examples: [] },
  { name: 'robot_name', kind: 'text', examples: [] },
  { name: 'date_of_collection', kind: 'time', examples: [] },
  { name: 'created_at', kind: 'time', examples: [] },
  { name: 'updated_at', kind: 'time', examples: [] },
  { name: 'species', kind: 'text', examples: [] },
  { name: 'weight', kind: 'number', examples: [] },
  { name: 'img_width', kind: 'number', examples: [] },
  { name: 'img_height', kind: 'number', examples: [] },
  { name: 'model_name', kind: 'text', examples: [] },
  { name: 'associated_error', kind: 'text', examples: [] },
  { name: 'tags', kind: 'text[]', examples: [] },
  { name: 'metrics', kind: 'json', examples: [] },
  { name: 'bbox.category', kind: 'text', examples: [] },
  { name: 'bbox.x', kind: 'number', examples: [] },
  { name: 'bbox.y', kind: 'number', examples: [] },
  { name: 'bbox.width', kind: 'number', examples: [] },
  { name: 'bbox.height', kind: 'number', examples: [] },
  { name: 'classification.label', kind: 'text', examples: [] },
  { name: 'classification.category', kind: 'text[]', examples: [] },
];

const RESULT_COLUMNS: Array<{ key: string; label: string }> = [
  { key: 'file_location', label: 'File' },
  { key: 'robot_name', label: 'Robot name' },
  { key: 'robot_type', label: 'Robot type' },
  { key: 'species', label: 'Species' },
  { key: 'tags', label: 'Tags' },
  { key: 'date_of_collection', label: 'Collected' },
  { key: 'type', label: 'Type' },
  { key: 'weight', label: 'Weight' },
  { key: 'img_width', label: 'Width' },
  { key: 'img_height', label: 'Height' },
  { key: 'model_name', label: 'Model' },
  { key: 'associated_error', label: 'Error' },
  { key: 'id', label: 'Id' },
  { key: 'created_at', label: 'Created' },
  { key: 'updated_at', label: 'Updated' },
  { key: 'metrics', label: 'Metrics' },
  { key: 'bboxes', label: 'Boxes' },
  { key: 'classifications', label: 'Classifications' },
];

const DEFAULT_COLUMNS = ['file_location', 'robot_name', 'robot_type', 'species', 'tags', 'date_of_collection'];

const PLACEHOLDER = "robot_name = 'PSDN-4'\nAND species IN ('SBL', 'RKF')\nAND 'train' = ANY(tags)";

const OPERATORS: Array<{ label: string; insert: string; arraysOnly?: boolean }> = [
  { label: '=', insert: '= ' },
  { label: '!=', insert: '!= ' },
  { label: '<', insert: '< ' },
  { label: '>', insert: '> ' },
  { label: '<=', insert: '<= ' },
  { label: '>=', insert: '>= ' },
  { label: 'IN', insert: 'IN (' },
  { label: 'LIKE', insert: "LIKE '" },
  { label: 'ILIKE', insert: "ILIKE '" },
  { label: 'IS NULL', insert: 'IS NULL ' },
  { label: 'IS NOT NULL', insert: 'IS NOT NULL ' },
  { label: 'BETWEEN', insert: 'BETWEEN ' },
  { label: '&& ARRAY', insert: "&& ARRAY['", arraysOnly: true },
];

function cellText(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(item => String(item)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function fieldByName(fields: QueryField[], name: string): QueryField | undefined {
  return fields.find(field => field.name.toLowerCase() === name.toLowerCase());
}

function openQuote(before: string): number {
  let quotes = 0;
  let start = -1;
  for (let i = 0; i < before.length; i += 1) {
    if (before[i] !== "'") continue;
    if (before[i + 1] === "'") {
      i += 1;
      continue;
    }
    quotes += 1;
    if (quotes % 2 === 1) start = i;
  }
  return quotes % 2 === 1 ? start : -1;
}

function columnForValue(before: string, fields: QueryField[]): QueryField | undefined {
  const pattern = /([A-Za-z_][A-Za-z0-9_.]*)\s*(?:=|!=|<>|<=|>=|LIKE|ILIKE|IN)\b/gi;
  let last = '';
  let match = pattern.exec(before);
  while (match) {
    last = match[1];
    match = pattern.exec(before);
  }
  if (!last) return undefined;
  return fieldByName(fields, last);
}

function lastWord(text: string): string {
  const match = /([A-Za-z_][A-Za-z0-9_.]*)$/.exec(text.trimEnd());
  return match ? match[1] : '';
}

function valueItems(field: QueryField, partial: string, replaceFrom: number, insideQuote: boolean): Suggestion[] {
  const needle = partial.toLowerCase();
  return field.examples
    .filter(value => value.toLowerCase().startsWith(needle))
    .slice(0, 8)
    .map(value => {
      const escaped = value.replace(/'/g, "''");
      return {
        label: value,
        insert: insideQuote ? `${escaped}' ` : `'${escaped}' `,
        replaceFrom,
      };
    });
}

function operatorItems(partial: string, replaceFrom: number, leadingSpace: boolean, arrayColumn: boolean): Suggestion[] {
  const needle = partial.toLowerCase();
  return OPERATORS
    .filter(op => arrayColumn || !op.arraysOnly)
    .filter(op => op.label.toLowerCase().startsWith(needle))
    .map(op => ({
      label: op.label,
      insert: `${leadingSpace ? ' ' : ''}${op.insert}`,
      replaceFrom,
    }));
}

function columnItems(fields: QueryField[], partial: string, replaceFrom: number): Suggestion[] {
  const needle = partial.toLowerCase();
  return fields
    .filter(field => field.name.toLowerCase().startsWith(needle) && field.name.toLowerCase() !== needle)
    .map(field => ({ label: field.name, insert: `${field.name} `, replaceFrom }));
}

function keywordItems(partial: string, replaceFrom: number, leadingSpace: boolean): Suggestion[] {
  const needle = partial.toLowerCase();
  return ['AND', 'OR', 'NOT']
    .filter(word => word.toLowerCase().startsWith(needle) && word.toLowerCase() !== needle)
    .map(word => ({
      label: word,
      insert: `${leadingSpace ? ' ' : ''}${word} `,
      replaceFrom,
    }));
}

function suggest(text: string, cursor: number, fields: QueryField[]): Suggestion[] {
  const before = text.slice(0, cursor);
  const quoteAt = openQuote(before);
  if (quoteAt >= 0) {
    const field = columnForValue(before.slice(0, quoteAt), fields);
    if (!field) return [];
    return valueItems(field, before.slice(quoteAt + 1), quoteAt, true);
  }
  const ident = /[A-Za-z_][A-Za-z0-9_.]*$/.exec(before);
  if (ident) {
    const partial = ident[0];
    const replaceFrom = cursor - partial.length;
    const previous = lastWord(before.slice(0, replaceFrom));
    const exact = fieldByName(fields, partial);
    if (exact) return operatorItems('', cursor, true, exact.kind === 'text[]');
    if (fieldByName(fields, previous)) {
      return operatorItems(partial, replaceFrom, false, fieldByName(fields, previous)?.kind === 'text[]');
    }
    return [...columnItems(fields, partial, replaceFrom), ...keywordItems(partial, replaceFrom, false)].slice(0, 12);
  }
  const trimmed = before.trimEnd();
  const previousField = fieldByName(fields, lastWord(trimmed));
  if (previousField && /\s$/.test(before)) {
    return operatorItems('', cursor, false, previousField.kind === 'text[]');
  }
  if (/(?:=|!=|<>|<=|>=)\s*$/.test(before) || /\b(?:LIKE|ILIKE)\s+$/i.test(before) || /\bIN\s*\(\s*$/i.test(before)) {
    const field = columnForValue(before, fields);
    if (!field) return [];
    return valueItems(field, '', cursor, false);
  }
  if (/\bIN\s*\([^)]*,\s*$/i.test(before)) {
    const field = columnForValue(before, fields);
    if (!field) return [];
    return valueItems(field, '', cursor, false);
  }
  if (!trimmed || /\b(?:AND|OR|NOT)\s+$/i.test(before) || /\(\s*$/.test(before)) {
    return columnItems(fields, '', cursor);
  }
  if (/'s*$/.test(trimmed) || /\bNULL\s*$/i.test(trimmed) || /\d$/.test(trimmed)) {
    const spaced = /\s$/.test(before);
    return keywordItems('', cursor, !spaced).filter(item => item.label !== 'NOT');
  }
  return [];
}

export default function QueryTab() {
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const whereRef = useRef('');
  const cursorRef = useRef(0);
  const [where, setWhere] = useState('');
  const [cursor, setCursor] = useState(0);
  const [fields, setFields] = useState<QueryField[]>(FALLBACK_FIELDS);
  const [columns, setColumns] = useState<string[]>(DEFAULT_COLUMNS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [focused, setFocused] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [result, setResult] = useState<{ columns: string[]; rows: Array<Record<string, unknown>>; limited: boolean } | null>(null);

  useEffect(() => {
    api.dataviewer.getQueryOptions()
      .then(res => {
        if (res.data.fields?.length) setFields(res.data.fields);
      })
      .catch(err => setError(err?.response?.data?.detail || err?.message || 'Could not load column values'));
  }, []);

  const items = suggest(where, cursor, fields);
  const suggestionKey = items.map(item => `${item.replaceFrom}:${item.insert}`).join('|');
  const selected = items.length ? Math.min(highlight, items.length - 1) : 0;
  const open = focused && !hidden && items.length > 0;

  useEffect(() => {
    setHighlight(0);
    setHidden(false);
  }, [suggestionKey]);

  const markCursor = (pos: number) => {
    cursorRef.current = pos;
    setCursor(pos);
  };

  const remember = (next: string, pos: number) => {
    whereRef.current = next;
    markCursor(pos);
    setWhere(next);
    requestAnimationFrame(() => {
      const node = areaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(pos, pos);
    });
  };

  const accept = (index: number) => {
    const item = items[index];
    if (!item) return;
    const current = whereRef.current;
    const at = cursorRef.current;
    const next = current.slice(0, item.replaceFrom) + item.insert + current.slice(at);
    remember(next, item.replaceFrom + item.insert.length);
  };

  const toggleColumn = (key: string) => {
    setColumns(current => (current.includes(key) ? current.filter(item => item !== key) : [...current, key]));
  };

  const run = async () => {
    setBusy(true);
    setError('');
    setHidden(true);
    try {
      const res = await api.dataviewer.searchQuery({ where: whereRef.current, columns });
      setResult(res.data);
    } catch (err: any) {
      setResult(null);
      setError(err?.response?.data?.detail || err?.message || 'Query failed');
    } finally {
      setBusy(false);
    }
  };

  const labels = Object.fromEntries(RESULT_COLUMNS.map(column => [column.key, column.label]));

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>Query</h2>
      <Card title="WHERE">
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <div style={{ color: tokens.textMuted, fontSize: '0.8rem', lineHeight: 1.45 }}>
            Type a WHERE clause. Suggestions follow the cursor, and Enter accepts the highlighted one. Ctrl+Enter runs the query.
          </div>
          <div style={{ position: 'relative' }}>
            <textarea
              ref={areaRef}
              value={where}
              spellCheck={false}
              placeholder={PLACEHOLDER}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onChange={event => {
                whereRef.current = event.target.value;
                setWhere(event.target.value);
                setFocused(true);
                markCursor(event.target.selectionStart ?? event.target.value.length);
              }}
              onClick={event => {
                setFocused(true);
                markCursor(event.currentTarget.selectionStart ?? 0);
              }}
              onKeyUp={event => {
                if (['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(event.key)) return;
                markCursor(event.currentTarget.selectionStart ?? 0);
              }}
              onKeyDown={event => {
                if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                  event.preventDefault();
                  if (!busy) run();
                  return;
                }
                if (!open) return;
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setHighlight(index => (index + 1) % items.length);
                } else if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  setHighlight(index => (index - 1 + items.length) % items.length);
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  setHidden(true);
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  accept(selected);
                }
              }}
              style={{
                width: '100%',
                minHeight: 140,
                resize: 'vertical',
                boxSizing: 'border-box',
                padding: '0.6rem 0.7rem',
                border: `1px solid ${tokens.border}`,
                borderRadius: tokens.radius,
                fontSize: '0.9rem',
                lineHeight: 1.5,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                color: tokens.text,
                background: tokens.surface,
              }}
            />
            {open && (
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '100%',
                  marginTop: 4,
                  zIndex: 5,
                  background: tokens.surface,
                  border: `1px solid ${tokens.border}`,
                  borderRadius: tokens.radius,
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                  maxHeight: 240,
                  overflow: 'auto',
                }}
              >
                {items.map((item, index) => (
                  <button
                    key={`${item.replaceFrom}-${item.label}`}
                    type="button"
                    onMouseDown={event => {
                      event.preventDefault();
                      accept(index);
                    }}
                    onMouseEnter={() => setHighlight(index)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '0.4rem 0.7rem',
                      border: 'none',
                      background: index === selected ? tokens.primarySoft : 'transparent',
                      color: index === selected ? tokens.primary : tokens.text,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                    }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem 0.9rem' }}>
            {RESULT_COLUMNS.map(column => (
              <label key={column.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.85rem' }}>
                <input
                  type="checkbox"
                  checked={columns.includes(column.key)}
                  onChange={() => toggleColumn(column.key)}
                />
                {column.label}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Button variant="primary" disabled={busy} onClick={run}>
              {busy ? 'Working…' : 'Run query'}
            </Button>
            {error && <span style={{ color: tokens.danger, fontSize: '0.85rem' }}>{error}</span>}
          </div>
        </div>
      </Card>
      <Card title="Results">
        {!result ? (
          <EmptyState>Type a WHERE clause, or leave it blank, then run the query.</EmptyState>
        ) : result.rows.length === 0 ? (
          <EmptyState>No rows matched.</EmptyState>
        ) : (
          <div style={{ display: 'grid', gap: '0.75rem' }}>
            <div style={{ color: tokens.textMuted, fontSize: '0.85rem' }}>
              {result.rows.length} rows{result.limited ? '. Showing the first 200.' : '.'}
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: 'max-content', minWidth: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    {result.columns.map(column => (
                      <th
                        key={column}
                        style={{
                          textAlign: 'left',
                          padding: '0.35rem 0.5rem',
                          borderBottom: `1px solid ${tokens.border}`,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {labels[column] || column}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row, index) => (
                    <tr key={index}>
                      {result.columns.map(column => (
                        <td
                          key={column}
                          style={{
                            padding: '0.35rem 0.5rem',
                            borderBottom: `1px solid ${tokens.border}`,
                            whiteSpace: 'nowrap',
                            maxWidth: 360,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {cellText(row[column])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
